import { EditorialStudy } from '../schema';

export const john1PrologueStudy: EditorialStudy = {
  schemaVersion: 1,
  id: 'john-1-1-18-before-bethlehem',
  version: '0.2.0-draft',
  locale: 'pt-BR',
  reference: {
    editionId: 'blivre-2018.2.0-tr',
    bookCode: 'JHN',
    chapter: 1,
    startVerse: 1,
    endVerse: 18,
  },
  title: 'Antes de Belém: a Palavra, a luz e o Deus que veio morar entre nós',
  lead: 'João 1:1–18 funciona como a porta de entrada do evangelho. Em poucos movimentos, o texto vai do “princípio” à criação, da luz ao testemunho, da rejeição à filiação, e finalmente à Palavra que se faz carne. O objetivo não é oferecer curiosidades soltas, mas preparar o leitor para reconhecer quem Jesus é quando a narrativa começar.',
  sources: [
    {
      id: 'blivre-john-1',
      kind: 'scripture',
      title: 'Bíblia Livre — João 1:1–18',
      edition: 'release 2018.2.0 / Novo Testamento Textus Receptus',
      locator: 'João 1:1–18',
      license: 'CC BY 3.0 Brasil (corpus integrado)',
    },
    {
      id: 'blivre-genesis-1',
      kind: 'scripture',
      title: 'Bíblia Livre — Gênesis 1:1–5',
      edition: 'release 2018.2.0',
      locator: 'Gênesis 1:1–5',
      license: 'CC BY 3.0 Brasil (corpus integrado)',
    },
    {
      id: 'step-john-1-1',
      kind: 'dataset',
      title: 'STEP Bible — TAGNT/TBESG, João 1:1',
      edition: 'snapshot b99716b0cddb648ddb95cc786a197180f2f97d48',
      locator: 'João 1:1 · λόγος (G3056)',
      license: 'CC BY 4.0',
    },
    {
      id: 'step-john-1-14',
      kind: 'dataset',
      title: 'STEP Bible — TAGNT/TBESG, João 1:14',
      edition: 'snapshot b99716b0cddb648ddb95cc786a197180f2f97d48',
      locator: 'João 1:14 · λόγος / σάρξ / σκηνόω',
      license: 'CC BY 4.0',
    },
  ],
  claims: [
    {
      id: 'john-prologue-identity',
      layer: 'interpretation',
      text: 'João apresenta a Palavra como já existente no princípio, em relação com Deus e identificada como Deus, antes de afirmar que ela se fez carne.',
      sourceIds: ['blivre-john-1'],
      certainty: 'high',
    },
    {
      id: 'john-logos-lexical',
      layer: 'context',
      text: 'Em João 1:1, o termo grego registrado para “Palavra” é λόγος (logos), lema λόγος, com gloss lexical breve “word”; o dado lexical identifica a palavra, mas não autoriza somar todos os sentidos possíveis do termo ao versículo.',
      sourceIds: ['step-john-1-1', 'blivre-john-1'],
      certainty: 'high',
    },
    {
      id: 'john-creation',
      layer: 'interpretation',
      text: 'O prólogo atribui à Palavra participação abrangente na criação: todas as coisas vieram a existir por meio dela.',
      sourceIds: ['blivre-john-1'],
      certainty: 'high',
    },
    {
      id: 'john-genesis-opening',
      layer: 'context',
      text: 'A abertura “No princípio”, seguida por criação, vida e luz, forma uma conexão literária observável com Gênesis 1:1–5.',
      sourceIds: ['blivre-john-1', 'blivre-genesis-1'],
      certainty: 'high',
    },
    {
      id: 'john-life-light',
      layer: 'interpretation',
      text: 'João liga vida e luz à Palavra e apresenta a luz brilhando nas trevas sem ser vencida por elas.',
      sourceIds: ['blivre-john-1'],
      certainty: 'high',
    },
    {
      id: 'john-baptist-witness',
      layer: 'interpretation',
      text: 'João Batista é apresentado como homem enviado para testemunhar da Luz; o texto o distingue explicitamente da própria Luz.',
      sourceIds: ['blivre-john-1'],
      certainty: 'high',
    },
    {
      id: 'john-reception-children',
      layer: 'interpretation',
      text: 'O prólogo contrasta rejeição e acolhimento: aos que recebem a Palavra e creem em seu nome é dado o direito de se tornarem filhos de Deus, nascimento atribuído a Deus e não à linhagem ou vontade humana.',
      sourceIds: ['blivre-john-1'],
      certainty: 'high',
    },
    {
      id: 'john-incarnation',
      layer: 'interpretation',
      text: 'O movimento decisivo do prólogo é que a Palavra se fez carne e habitou entre os seres humanos; João não descreve uma aparência de humanidade, mas usa a linguagem concreta de “carne”.',
      sourceIds: ['blivre-john-1', 'step-john-1-14'],
      certainty: 'high',
    },
    {
      id: 'john-dwell-lexical',
      layer: 'context',
      text: 'Em João 1:14, o registro grego inclui ἐσκήνωσεν, forma do verbo σκηνόω, com gloss breve “to dwell”. O dado sustenta “habitar/morar”; conexões teológicas adicionais precisam de evidência própria e não são tratadas como significado secreto da palavra.',
      sourceIds: ['step-john-1-14', 'blivre-john-1'],
      certainty: 'high',
    },
    {
      id: 'john-grace-truth',
      layer: 'interpretation',
      text: 'Os versículos 14–17 associam a Palavra encarnada a glória, graça e verdade e contrastam a lei dada por meio de Moisés com graça e verdade vindo por meio de Jesus Cristo.',
      sourceIds: ['blivre-john-1'],
      certainty: 'high',
    },
    {
      id: 'john-reveals-father',
      layer: 'interpretation',
      text: 'O fechamento do prólogo afirma que o Filho torna Deus conhecido, preparando o evangelho para apresentar Jesus como revelador do Pai.',
      sourceIds: ['blivre-john-1'],
      certainty: 'high',
    },
    {
      id: 'john-application-response',
      layer: 'application',
      text: 'Dentro do próprio prólogo, a resposta humana central não é dominar uma curiosidade sobre o texto, mas receber aquele que veio ao mundo e crer em seu nome.',
      sourceIds: ['blivre-john-1'],
      certainty: 'high',
    },
  ],
  sections: [
    {
      id: 'identity',
      eyebrow: '1 · QUEM É A PALAVRA',
      title: 'João começa antes de Belém',
      body: 'O evangelho não começa explicando onde Jesus nasceu, mas quem ele é. A Palavra já “era” no princípio, estava junto de Deus e é chamada Deus. Isso muda a leitura de tudo que vem depois: os sinais, as conversas, a cruz e a ressurreição serão atos daquele que o prólogo já colocou antes da criação.',
      layer: 'interpretation',
      claimIds: ['john-prologue-identity', 'john-logos-lexical'],
    },
    {
      id: 'creation',
      eyebrow: '2 · CRIAÇÃO, VIDA E LUZ',
      title: 'O começo de João conversa com o começo da Bíblia',
      body: '“No princípio” não fica sozinho. Logo aparecem criação, vida, luz e trevas. A ligação com Gênesis não depende de códigos escondidos: está na própria sequência de imagens. João apresenta Jesus dentro do horizonte do Criador e da vida que vence a escuridão.',
      layer: 'context',
      claimIds: ['john-creation', 'john-genesis-opening', 'john-life-light'],
    },
    {
      id: 'witness',
      eyebrow: '3 · O TESTEMUNHO',
      title: 'João Batista aponta; ele não ocupa o centro',
      body: 'No meio de afirmações enormes sobre a Palavra, o texto introduz um homem chamado João. A função dele é cuidadosamente delimitada: testemunhar para que outros creiam. Ele não é a Luz. Isso cria um padrão importante para o evangelho: a testemunha é valiosa justamente quando conduz o olhar para Cristo.',
      layer: 'interpretation',
      claimIds: ['john-baptist-witness'],
    },
    {
      id: 'response',
      eyebrow: '4 · REJEIÇÃO E ACOLHIMENTO',
      title: 'Estar perto da luz não é o mesmo que recebê-la',
      body: 'A Palavra vem ao mundo que foi feito por meio dela e, ainda assim, não é reconhecida por todos. O contraste muda nos versículos 12–13: receber e crer são ligados ao direito de tornar-se filho de Deus. João desloca a identidade espiritual de herança humana para ação de Deus.',
      layer: 'interpretation',
      claimIds: ['john-reception-children'],
    },
    {
      id: 'incarnation',
      eyebrow: '5 · A PALAVRA SE FEZ CARNE',
      title: 'O eterno entra na condição humana',
      body: 'O texto que começou no princípio chega a uma frase surpreendentemente concreta: “a Palavra se fez carne”. O dado grego confirma a linguagem de carne e de habitar. A força do versículo não precisa de misticismo lexical: João coloca lado a lado a identidade apresentada nos primeiros versos e uma presença verdadeiramente humana entre nós.',
      layer: 'interpretation',
      claimIds: ['john-incarnation', 'john-dwell-lexical'],
    },
    {
      id: 'grace-truth',
      eyebrow: '6 · GRAÇA E VERDADE',
      title: 'A glória que João quer que o leitor enxergue',
      body: 'Depois da encarnação, o prólogo fala de glória, plenitude, graça e verdade. Moisés e a lei aparecem dentro desse movimento, e Jesus Cristo é finalmente nomeado. O texto não trata graça como ausência de verdade nem verdade como falta de graça; as duas são colocadas juntas na descrição do que chega por meio de Cristo.',
      layer: 'interpretation',
      claimIds: ['john-grace-truth'],
    },
    {
      id: 'revelation',
      eyebrow: '7 · O PAI É TORNADO CONHECIDO',
      title: 'O prólogo termina dizendo para onde olhar',
      body: 'O versículo 18 fecha a abertura com a ideia de revelação: o Filho torna Deus conhecido. Isso funciona como uma chave para continuar o evangelho. Quando Jesus agir, falar, acolher, confrontar e entregar a vida, João quer que o leitor veja nesses atos a revelação do Pai.',
      layer: 'interpretation',
      claimIds: ['john-reveals-father'],
    },
    {
      id: 'application',
      eyebrow: '8 · PARA LEVAR À VIDA',
      title: 'O texto pede mais que admiração',
      body: 'João 1 permite muitas perguntas profundas, mas o próprio prólogo oferece sua resposta existencial: receber e crer. O estudo do contexto, das palavras e das conexões bíblicas só cumpre seu papel quando ajuda o leitor a enxergar com mais clareza aquele para quem o texto aponta.',
      layer: 'application',
      claimIds: ['john-application-response'],
    },
  ],
  review: {
    status: 'draft',
    reviewer: null,
    reviewedAt: null,
  },
};
